import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { serializeError } from 'serialize-error';

import { Log, ParsedError } from 'services/logger.service';
import { LogLevel } from 'services/logger.service';
import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { getPath, Path } from 'components/_hooks/use-router';
import { Error } from 'components/app/_helpers/app-errors.helper';

const SLICE_ID = 'app__logger';
const RECENT_ACTIONS_LEN = 99;

export type UserActionKey = 'NAV' | 'BACKUP' | 'SERV' | 'EVENT' | 'USER';

interface UserAction {
  key: UserActionKey;
  desc: string;
  payload?: Record<string, unknown>;
}

export interface UserActionData extends UserAction {
  time: string;
  path: Path;
}

export interface LogPayload {
  level?: LogLevel;
  error: ParsedError;
}

interface State {
  recentActions: UserActionData[];
}

const initialState: State = {
  recentActions: [],
};

// T H U N K S

export const appLogger = createAsyncThunk(
  SLICE_ID + '/log',
  async (payload: LogPayload, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { selectedFinalcut, selectedProject, selectedStory } = state.projects;
      const finalCutVersionId = state.fc__data.id;
      const userId = state.app__data.user?.id;
      if (!userId) return;
      await Log({
        userId,
        projectId: selectedProject,
        storyId: selectedStory,
        finalCutId: selectedFinalcut,
        finalCutVersionId,
        level: payload.level ?? LogLevel.Info,
        error: payload.error,
      });
    } catch (error) {
      return rejectWithValue(Error.reduxErrorHandler(error));
    }
  }
);

export const appCrashLogger = createSafeAsyncThunk(
  SLICE_ID + '/appCrashLogger',
  async (payload: { error: Error }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const backup = state.backup;
    const recentActions = state.app__logger.recentActions;
    const recentBackupPayload = backup.backups[backup.index];
    dispatch(
      appLogger({
        level: LogLevel.Fatal,
        error: {
          name: 'appCrashLogger',
          message: JSON.stringify({ recentActions, recentBackupPayload, error: serializeError(payload.error) }),
        },
      })
    );
  }
);

// S L I C E

export const backupSlice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setRecentAction: (state, action: PayloadAction<UserAction>) => {
      state.recentActions.push({ ...action.payload, time: new Date().toISOString(), path: getPath() });
      if (state.recentActions.length === RECENT_ACTIONS_LEN) state.recentActions.shift();
    },
    cleanRecentActions: (state) => {
      state.recentActions = [];
    },
  },
});

export const { setRecentAction, cleanRecentActions } = backupSlice.actions;
export default backupSlice.reducer;
